<template>
  <section class="optional-meeting-scheduling">
    <div class="schedule-meeting-heading-container">
      <h3>Schedule Meeting (Optional)</h3>
      <k-toggle class="schedule-meeting-toggle" v-model="scheduleMeetingToggle" size="small"/>
    </div>
    <p>
      You can schedule the meeting now or later.
      If not scheduled now, this meeting will appear in the inbox of the learner's
      <span class="support-function">{{ meetingOwner.learner_support_type }}</span>, under the
      <strong>Meetings to Schedule</strong> tab.
    </p>
    <p>
      If you schedule the meeting now, it will appear in the inbox of the
      <span class="support-function">Meeting Owner</span> set below.
    </p>
    <template v-if="scheduleMeetingToggle">
      <div class="meeting-owner-body">
        <div class="meeting-owner-heading-container">
          <h4>Assign Meeting Owner</h4>
          <include-line-manager-toggle
            v-model="includeLineManager"
            :line-manager="lineManager"
          />
        </div>
        <div class="meeting-owner-cards-layout">
          <select-user
              v-model="meetingOwner"
              :default-meeting-owner="defaultMeetingOwner"
              :split-profile-cards="showLineManagerCard"
              @reset-meeting-owner="meetingOwner = $event"
          >
            <template v-if="showLineManagerCard" #adjacent-profile>
              <user-profile-card
                class="line-manager-card"
                :name="lineManager.name"
                :email="lineManager.email"
                role="Line Manager"
                :userId="lineManager.user_id"
              />
            </template>
          </select-user>
        </div>
      </div>
      <hr class="horizontal-hr">
      <form class="optional-meeting-scheduling-form">
        <set-meeting-datetime
          v-model="schedule"
          dropdown-upward
          @validation="onDatetimeValidation"
        />
        <set-meeting-link
          v-model="meetingLink"
          input-id="meeting-schedule-link"
          :learner-user-id="learnerUserId"
          :meeting-id="meetingId"
          :is-ad-hoc="true"
          :start-time="meetingStartDate"
          :end-time="meetingEndDate"
          @validation="meetingLinkValid = $event.isValid"
          @teams-metadata-changed="onTeamsMetadataChanged"
        />
      </form>
      <meeting-description-section
        class="ad-hoc-meeting-description"
        v-model="meetingDescription"
        field-id="ad-hoc-meeting-description"
      />
    </template>
  </section>
</template>

<style scoped>
.optional-meeting-scheduling-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.ad-hoc-meeting-description {
  margin-top: 15px;
}

.meeting-owner-heading-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
}

.meeting-owner-heading-container :deep(.include-line-manager) {
  margin-bottom: 0;
}
</style>

<script>
import TimeMixin from '@mixins/time-mixins';
import useGlobalStore from '@stores/global';
import KToggle from '@base-components/forms/k-toggle.vue';
import UserProfileCard from '@base-components/user-profile-card.vue';
import SelectUser from './select-user.vue';
import IncludeLineManagerToggle from './include-line-manager-toggle.vue';
import SetMeetingDatetime from './set-meeting-datetime.vue';
import SetMeetingLink from './set-meeting-link.vue';
import MeetingDescriptionSection from './meeting-description-section.vue';
import isValidLineManager from '../utils/line-manager-utils';

export default {
  mixins: [TimeMixin],

  components: {
    SelectUser,
    KToggle,
    MeetingDescriptionSection,
    UserProfileCard,
    IncludeLineManagerToggle,
    SetMeetingDatetime,
    SetMeetingLink,
  },

  props: {
    modelValue: {
      type: Object,
    },
    lineManager: {
      type: Object,
      default: null,
    },
    learnerUserId: {
      type: Number,
      default: null,
    },
    meetingId: {
      type: Number,
      default: null,
    },
  },

  mounted() {
    this.meetingOwner = this.modelValue || this.defaultMeetingOwner;
  },

  data() {
    const meetingDate = new Date();
    const startTime = new Date();
    return {
      store: useGlobalStore(),
      showUserSearch: false,
      scheduleMeetingToggle: false,
      schedule: {
        meetingDate,
        startTime,
        endTime: null,
      },
      datetimeValid: false,
      meetingLink: '',
      meetingLinkValid: false,
      meetingDescription: '',
      selectedMeetingOwner: undefined,
      scheduleMeeting: false,
      meetingOwner: {},
      meetingOwnerId: undefined,
      meetingOwnerRole: undefined,
      meetingName: undefined,
      isValidScheduledForm: false,
      includeLineManager: false,
      teamsMeetingId: null,
      teamsOrganiserUserId: null,
    };
  },

  watch: {
    meetingData: {
      handler(value) {
        this.$emit('meeting-data', value);
      },
      immediate: true,
    },
    isValidForm(value) {
      this.$emit('validation', value);
    },
  },

  computed: {
    meetingStartDate() {
      if (!this.schedule.meetingDate || !this.schedule.startTime) {
        return null;
      }
      return this.overrideDateComponent(this.schedule.startTime, this.schedule.meetingDate);
    },
    meetingEndDate() {
      if (!this.schedule.meetingDate || !this.schedule.endTime) {
        return null;
      }
      return this.overrideDateComponent(this.schedule.endTime, this.schedule.meetingDate);
    },
    meetingData() {
      return {
        meetingOwner: this.meetingOwner,
        meetingDate: this.schedule.meetingDate,
        startTime: this.meetingStartDate,
        endTime: this.meetingEndDate,
        meetingLink: this.meetingLink,
        meetingDescription: this.meetingDescription,
        scheduleMeeting: this.scheduleMeetingToggle,
        meetingOwnerId: this.meetingOwner.id,
        meetingOwnerRole: this.meetingOwner.learner_support_type,
        meetingName: this.meetingOwner.full_name,
        isValidScheduledForm: this.isValidForm,
        includeLineManager: this.includeLineManager,
        teamsMeetingId: this.teamsMeetingId,
        teamsOrganiserUserId: this.teamsOrganiserUserId,
      };
    },
    defaultMeetingOwner() {
      return {
        id: this.store.userId,
        full_name: this.store.userFullName,
        email: this.store.userEmail,
        avatar_url: this.store.avatarUrl,
        learner_support_type: this.store.learnerSupportType,
      };
    },
    isValidForm() {
      if (this.scheduleMeetingToggle) {
        return this.datetimeValid && this.meetingLinkValid;
      }
      return true;
    },
    showLineManagerCard() {
      return this.$featureFlags.isEnabled('meetings-inbox-gcal-integration')
        && this.includeLineManager
        && isValidLineManager(this.lineManager);
    },
  },

  methods: {
    handleUserSelection(userId) {
      this.selectedMeetingOwner = userId;
      this.$emit('meeting-owner-selected', userId);
      this.showUserSearch = false;
    },
    onDatetimeValidation({ isValid }) {
      this.datetimeValid = isValid;
    },
    onTeamsMetadataChanged(metadata) {
      this.teamsMeetingId = metadata?.teamsMeetingId ?? null;
      this.teamsOrganiserUserId = metadata?.teamsOrganiserUserId ?? null;
    },
    emitMeetingData() {
      this.$emit('meeting-data', this.meetingData);
    },
  },
};
</script>
